import { Component, OnDestroy } from '@angular/core';
import { Observable, Subscription } from 'rxjs';
import { map } from 'rxjs/operators';
import { JobProgressService } from '../../../core/services/job-progress.service';
import {
  JOB_TYPE_PC_RESET,
  JobProgressState,
} from '../../../core/models/job-progress';
import { PopupDialogService } from '../../popup-dialog/popup-dialog.service';
import {
  FAILED,
  JOB_PROGRESS_CARD_SUBTITLE,
  JOB_PROGRESS_RUN_IN_BACKGROUND_BTN,
  JOB_PROGRESS_STATUS_SUCCESS,
  JOB_PROGRESS_TOAST_HINT,
  PC_RESET_COMPLETED_HEADER,
  PC_RESET_COMPLETED_MSG,
  PC_RESET_DONE_BTN,
  PC_RESET_FAILED_HEADER,
  PC_RESET_FAILED_MSG,
  PC_RESET_MODAL_SUBTITLE,
} from '../../constants';

const RESET_LABEL_PREFIX = 'Resetting data for ';

/**
 * SYRC-7180: app-shell background-job progress UI. Mounted at the root so it
 * survives route changes and full page reloads (via JobProgressService's active-
 * job markers). Bound to the shared, multi-job stream and demuxed by jobId:
 *  - the first non-minimized job renders as a centered modal (with "Run in
 *    background");
 *  - minimized jobs render as stacked bottom-right toasts.
 *
 * SYRC-7421: also hosts the OUTCOME dialog of the company data reset ("Data reset" / "Reset failed"),
 * because it must appear whichever page the admin is on — and after a reload that resumed the job.
 */
@Component({
  selector: 'app-pc-deletion-toast',
  templateUrl: './pc-deletion-toast.component.html',
  styleUrl: './pc-deletion-toast.component.css',
})
export class PcDeletionToastComponent implements OnDestroy {
  /** The one centered modal (first non-minimized job), or null. */
  readonly modalJob$: Observable<JobProgressState | null>;
  /** Minimized jobs shown as toasts. */
  readonly toastJobs$: Observable<JobProgressState[]>;

  readonly runInBackgroundLabel = JOB_PROGRESS_RUN_IN_BACKGROUND_BTN;
  readonly subtitle = JOB_PROGRESS_CARD_SUBTITLE;
  readonly toastHint = JOB_PROGRESS_TOAST_HINT;

  private readonly terminalSub: Subscription;

  constructor(
    private jobProgress: JobProgressService,
    private popupDialogService: PopupDialogService,
  ) {
    this.modalJob$ = this.jobProgress.activeJobs$.pipe(
      map((jobs) => jobs.find((j) => !j.minimized) ?? null),
    );
    this.toastJobs$ = this.jobProgress.activeJobs$.pipe(
      map((jobs) => jobs.filter((j) => j.minimized)),
    );
    this.terminalSub = this.jobProgress.terminal$.subscribe((job) =>
      this.onJobTerminal(job),
    );
  }

  ngOnDestroy(): void {
    this.terminalSub.unsubscribe();
  }

  trackByJobId(_index: number, job: JobProgressState): string {
    return job.jobId;
  }

  runInBackground(jobId: string): void {
    this.jobProgress.minimize(jobId);
  }

  private isReset(job: JobProgressState): boolean {
    return job.jobType === JOB_TYPE_PC_RESET;
  }

  /** Modal heading: the reset names the company in quotes ("Resetting data for “Acme”"). */
  titleFor(job: JobProgressState): string {
    if (this.isReset(job) && job.label.startsWith(RESET_LABEL_PREFIX)) {
      return `${RESET_LABEL_PREFIX}“${job.label.slice(RESET_LABEL_PREFIX.length)}”`;
    }
    return job.label;
  }

  subtitleFor(job: JobProgressState): string {
    return this.isReset(job) ? PC_RESET_MODAL_SUBTITLE : this.subtitle;
  }

  /** Current phase, with the trailing ellipsis the reset design shows ("Removing assets…"). */
  phaseFor(job: JobProgressState): string {
    return this.isReset(job) && job.phase && !job.phase.endsWith('…')
      ? `${job.phase}…`
      : job.phase;
  }

  // SYRC-7421: the reset reports its outcome in a dialog (JobProgressService deliberately skips its
  // toastr for this job type). A failed run shows the server's detail — which phase failed and what is
  // left — falling back to the generic text, and tells the admin the reset can simply be run again.
  private onJobTerminal(job: JobProgressState): void {
    if (!this.isReset(job)) {
      return;
    }
    const succeeded =
      (job.status ?? '').toLowerCase() ===
      JOB_PROGRESS_STATUS_SUCCESS.toLowerCase();
    if (succeeded) {
      const name = job.label.startsWith(RESET_LABEL_PREFIX)
        ? job.label.slice(RESET_LABEL_PREFIX.length)
        : job.label;
      this.popupDialogService.openDialog(
        PC_RESET_COMPLETED_HEADER,
        job.message || PC_RESET_COMPLETED_MSG(name),
        'success',
        PC_RESET_DONE_BTN,
      );
    } else {
      this.popupDialogService.openDialog(
        PC_RESET_FAILED_HEADER,
        job.message || PC_RESET_FAILED_MSG,
        FAILED,
        PC_RESET_DONE_BTN,
      );
    }
  }
}
